import { useState } from 'react';
import { compositeHistory, myCompositeHistory } from '@aithora/shared';
import { sx } from '../../ui/designStyles';
import { usePerf } from './ctx';
import { gradeOf, notServed, pre, usePerfApi } from './lib';
import { BenchFrame, benchRows, ownRow, setBenchUi, useBenchUi } from './portfolioHead';
import { TrajectoryChart, forecast, type TrajSeries } from './trajectoryChart';
import { BuildLabel, useBuildText, useCustomerText } from '../../ui/buildLabels';

/** One line per building, in the design's order of colours (design palette, L37042). */
const PALETTE = ['#2F5C97', '#1F6B52', '#5B6B7C', '#44505E', '#3C8BA5', '#A85F52', '#5A7D4E', '#8E7B4A', '#6E5B3A'];
const V = (c: string) => 'font-size:16px;font-weight:800;font-variant-numeric:tabular-nums;white-space:nowrap;color:' + c + ';';
const LABEL = 'display:block;font-variant-numeric:tabular-nums;font-size:14px;letter-spacing:.12em;text-transform:uppercase;color:#54606C;font-weight:600;';

/** Points and grade to the next band, or null at the top (design nextGrade, L34070). */
function nextGrade(c: number): { pts: number; grade: string } | null {
  if (c >= 80) return null;
  const up: [number, string] = c >= 72 ? [80, 'A'] : c >= 62 ? [72, 'B'] : c >= 52 ? [62, 'C'] : [52, 'D'];
  return { pts: up[0] - c, grade: up[1] };
}
const medianOf = (v: number[]) => (v.length ? (v.length % 2 ? v[(v.length - 1) / 2] : Math.round((v[v.length / 2 - 1] + v[v.length / 2]) / 2)) : 0);

/**
 * Trajectory (design bTrend, T16505-16559, under the shared bench sub-header T16457-16468):
 * each building's composite over eight trailing quarters, read a quarter by hover or tap,
 * isolate a building by clicking it, and a +30d strip that is MODELED (the last three
 * quarters' slope carried 30 days forward, computed here). A Facility Admin sees their own
 * building only.
 */
export function TrajectoryTab() {
  const ctx = usePerf();
  const { op, peerLabel, tier, caseMix, facilities } = ctx;
  const hist = usePerfApi(
    (c, signal) => (op ? compositeHistory : myCompositeHistory)(c, { query: { quarters: 8, tier, caseMix: caseMix || undefined, facilities: op ? facilities : undefined }, signal }),
    [op, tier, caseMix, facilities],
  );
  // openRow is shared with Portfolio and Distribution (design openRow), so a building
  // isolated here opens expanded there.
  const u = useBenchUi();
  const bt = useBuildText();
  const plain = useCustomerText();
  const openRow = u.openRow;
  const [hover, setHover] = useState<number | null>(null);
  const [pinned, setPinned] = useState<number | null>(null);

  const h = hist.data;
  const quarters = h?.quarters ?? [];
  const complete = (c: (number | null)[] | undefined): c is number[] => !!c && c.length === quarters.length && c.length > 1 && c.every((v) => v != null);
  const own = op ? null : ownRow(ctx);
  const base = op ? benchRows(ctx, u).shown : own ? [own] : [];
  const rows: { key: string; name: string; pts: number[] }[] = base.flatMap((r) => {
    const c = h?.facilities.find((x) => (x.orgId ?? 'own') === r.id)?.composites;
    return complete(c) ? [{ key: r.id, name: r.name, pts: c }] : [];
  });
  const anySelected = rows.some((r) => r.key === openRow);
  const series: TrajSeries[] = rows.map((r, i) => ({ ...r, on: openRow === r.key, color: PALETTE[i % PALETTE.length] }));
  const n = quarters.length;
  const qIdx = hover === null ? n - 1 : hover;
  const qVals = series.map((s) => Math.round(s.pts[qIdx])).sort((a, b) => a - b);
  const qMedian = medianOf(qVals);
  const peerMed = h?.peerMedian ?? ctx.peer?.median ?? qMedian;

  const toggle = (key: string) => setBenchUi({ openRow: openRow === key ? null : key });
  const movers: { k: string; name: string; v: string; color: string; tip: string; onClick: (() => void) | null }[] = (() => {
    if (!series.length) return [];
    if (!op) {
      const s0 = series[0];
      const cur = Math.round(s0.pts[n - 1]);
      const gap = cur - peerMed;
      const qd = Math.round(s0.pts[n - 1] - s0.pts[n - 2]);
      const nx = nextGrade(cur);
      const f = Math.round(forecast(s0.pts));
      return [
        { k: 'Vs typical for peers', name: 'typical for peers ' + peerMed, v: (gap >= 0 ? '+' : '') + gap, color: gap >= 0 ? '#1F6B52' : '#B3261E', tip: 'Your composite against the ' + peerMed + ' median of ' + peerLabel, onClick: null },
        { k: 'This quarter', name: 'change since last quarter', v: qd === 0 ? '–' : (qd > 0 ? '+' : '') + qd, color: qd > 0 ? '#1F6B52' : qd < 0 ? '#B3261E' : '#54606C', tip: 'Measured', onClick: null },
        { k: 'Next grade', name: nx ? nx.pts + ' points to ' + nx.grade : 'top band', v: gradeOf(cur), color: '#22272E', tip: 'Grade steps at 52 / 62 / 72 / 80', onClick: null },
        {
          k: 'In 30 days',
          name: bt((gradeOf(f) !== gradeOf(cur) ? 'projected to cross into ' + gradeOf(f) : 'no grade change projected') + ' · MODELED EST.'),
          v: String(f),
          color: f > cur ? '#1F6B52' : f < cur ? '#B3261E' : '#54606C',
          tip: bt('MODELED EST.: last-three-quarter slope carried 30 days forward'),
          onClick: null,
        },
      ];
    }
    const q = series.map((s) => {
      const d = Math.round(s.pts[n - 1] - s.pts[n - 2]);
      return { s, d, g0: gradeOf(Math.round(s.pts[n - 1])), g1: gradeOf(Math.round(forecast(s.pts))) };
    });
    const up = q.slice().sort((a, b) => b.d - a.d)[0];
    const dn = q.slice().sort((a, b) => a.d - b.d)[0];
    const cross = q.find((x) => x.g0 !== x.g1);
    const out: typeof movers = [];
    if (up && up.d > 0) out.push({ k: 'Biggest gain', name: up.s.name, v: '+' + up.d, color: '#1F6B52', tip: 'Isolate ' + up.s.name + ' on the chart', onClick: () => toggle(up.s.key) });
    if (dn && dn.d < 0) out.push({ k: 'Biggest drop', name: dn.s.name, v: String(dn.d), color: '#B3261E', tip: 'Isolate ' + dn.s.name + ' on the chart', onClick: () => toggle(dn.s.key) });
    out.push(
      cross
        ? { k: 'Crossing a grade', name: cross.s.name, v: cross.g0 + ' → ' + cross.g1, color: cross.g1 < cross.g0 ? '#1F6B52' : '#B3261E', tip: 'MODELED: last-three-quarter slope carried 30 days forward', onClick: () => toggle(cross.s.key) }
        : { k: 'Crossing a grade', name: 'None projected in 30 days', v: '', color: '#54606C', tip: bt('MODELED'), onClick: null },
    );
    return out;
  })();

  const note =
    'Dotted verticals mark CMS methodology changes: scores are not strictly comparable across them. ' +
    (op ? 'Shaded envelope is the portfolio min–max range. ' : '') +
    'The blue strip past the last quarter is MODELED: ' +
    (op ? 'each line’s' : 'your') +
    ' last-three-quarter slope carried 30 days forward, not a measurement.';
  const loading = hist.loading && !h;

  return (
    <BenchFrame label="Trajectory">
      <div data-testid="perf-trajectory">
        <div style={sx('border-top:1px solid rgba(34,39,46,.14);padding:18px 20px;margin-top:16px;')} aria-busy={loading ? 'true' : undefined}>
          <div style={sx('display:flex;align-items:baseline;gap:10px;flex-wrap:wrap;row-gap:6px;')}>
            <span style={sx('font-size:16px;font-weight:700;letter-spacing:-.01em;')}>Composite trajectory</span>
            <span style={sx('font-size:16px;color:#54606C;')}>
              <span>{op ? 'eight trailing quarters · hover or tap to read a quarter · click a facility to isolate it' : 'eight trailing quarters · hover or tap to read a quarter'}</span>
            </span>
          </div>
          {hist.error && !h ? (
            <div style={sx('font-size:16px;color:' + (notServed(hist.error) ? '#54606C' : '#B3261E') + ';margin-top:10px;')} role={notServed(hist.error) ? undefined : 'alert'} data-testid="empty-state">
              {notServed(hist.error) ? plain('Not served yet: the composite per building per quarter (the benchmark history).') : hist.error.message}
            </div>
          ) : h && !series.length ? (
            <div style={sx('font-size:16px;color:#54606C;margin-top:10px;')} data-testid="empty-state">
              No quarterly composites for {op ? 'these buildings' : 'your building'} yet.
            </div>
          ) : !h ? null : (
            <div style={sx('display:flex;flex-wrap:wrap;gap:20px;margin-top:10px;align-items:flex-start;')}>
              <div style={sx('flex:1.6 1 440px;min-width:0;')}>
                <div>
                  <TrajectoryChart series={series} quarters={quarters} peerMed={peerMed} markers={h.markers} hover={hover} pinned={pinned} onHover={setHover} onPin={setPinned} />
                </div>
                <div style={sx('font-size:14px;font-variant-numeric:tabular-nums;color:#54606C;line-height:1.5;margin-top:4px;')}>
                  <span>{note}</span>
                </div>
              </div>
              <div style={sx('flex:1 1 320px;border-left:1px solid rgba(34,39,46,.1);padding-left:18px;min-width:0;')} data-testid="traj-panel">
                <div style={sx('display:flex;align-items:baseline;gap:8px;flex-wrap:wrap;row-gap:6px;')}>
                  <span style={sx('font-size:19px;font-weight:700;letter-spacing:-.02em;')}>
                    <span>{hover === null ? 'Latest · ' + quarters[n - 1] : quarters[hover]}</span>
                  </span>
                  <span style={sx('flex:1;')} />
                  <a
                    href="#"
                    onClick={(e) => {
                      pre(e);
                      setBenchUi({ openRow: null });
                    }}
                    style={sx(anySelected ? 'display:inline-block;font-variant-numeric:tabular-nums;font-size:14px;letter-spacing:.1em;text-transform:uppercase;font-weight:600;' : 'display:none;')}
                  >
                    Clear
                  </a>
                </div>
                <div style={sx('font-size:16px;color:#54606C;margin-top:3px;')}>
                  <span>{hover === null ? 'hover or tap the chart to read any quarter' : pinned === hover ? 'pinned · tap again to release' : 'reading ' + quarters[hover]}</span>
                </div>
                <div style={sx('display:flex;gap:22px;margin-top:12px;padding-bottom:12px;border-bottom:1px solid rgba(34,39,46,.1);')}>
                  <span>
                    <span style={sx(LABEL)}>
                      <span>{op ? 'Typical' : 'Your composite'}</span>
                    </span>
                    <span data-countup="1" style={sx('display:block;font-size:19px;font-weight:700;margin-top:2px;')}>
                      <span>{String(qMedian)}</span>
                    </span>
                  </span>
                  {op && (
                    <span>
                      <span style={sx(LABEL)}>Spread</span>
                      <span style={sx('display:block;font-size:19px;font-weight:700;margin-top:2px;')}>
                        <span>{(qVals[0] ?? 0) + ' – ' + (qVals[qVals.length - 1] ?? 0)}</span>
                      </span>
                    </span>
                  )}
                </div>
                <div style={sx('display:grid;grid-template-columns:10px minmax(0,1fr) 58px 30px 32px 50px;gap:8px;padding:8px 8px 4px;font-variant-numeric:tabular-nums;font-size:14px;letter-spacing:.08em;text-transform:uppercase;color:#54606C;font-weight:600;align-items:end;')}>
                  <span />
                  <span>Facility</span>
                  <span>8Q</span>
                  <span style={sx('text-align:right;')}>Val</span>
                  <span style={sx('text-align:right;')}>{'Δ'}</span>
                  <span style={sx('text-align:right;line-height:1.1;')}>
                    +30d<BuildLabel><span style={sx('display:block;font-size:14px;font-weight:800;color:#2F5C97;letter-spacing:.06em;')}>MODELED</span></BuildLabel>
                  </span>
                </div>
                {series.map((s) => {
                  const val = Math.round(s.pts[qIdx]);
                  const d = Math.round(s.pts[qIdx] - s.pts[0]);
                  const lo8 = Math.min(...s.pts);
                  const rg = Math.max(1, Math.max(...s.pts) - lo8);
                  const last = Math.round(s.pts[n - 1]);
                  const fcv = Math.round(forecast(s.pts));
                  const color = s.on ? s.color : anySelected ? 'rgba(34,39,46,.25)' : s.color;
                  return (
                    <a
                      key={s.key}
                      href="#"
                      aria-pressed={s.on}
                      data-testid="traj-row"
                      onClick={(e) => {
                        pre(e);
                        toggle(s.key);
                      }}
                      style={sx(
                        'display:grid;grid-template-columns:10px minmax(0,1fr) 58px 30px 32px 50px;gap:8px;align-items:center;padding:5px 8px;border-radius:7px;cursor:pointer;font-size:16px;text-decoration:none;color:#22272E;' +
                          (s.on ? 'background:rgba(79,138,214,.1);font-weight:700;' : ''),
                      )}
                    >
                      <span style={sx('width:10px;height:10px;border-radius:2px;background:' + color + ';')} />
                      <span style={sx('min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;')}>
                        <span>{s.name}</span>
                      </span>
                      <svg width="58" height="18" viewBox="0 0 58 18" style={sx('display:block;overflow:visible;')} aria-hidden="true">
                        <path
                          d={s.pts.map((p, i) => (i ? 'L' : 'M') + (1 + (i * 56) / (n - 1)).toFixed(1) + ' ' + (16 - ((p - lo8) / rg) * 14).toFixed(1)).join(' ')}
                          fill="none"
                          stroke={color}
                          strokeWidth="1.6"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                      <span style={sx('font-variant-numeric:tabular-nums;font-size:16px;font-weight:700;text-align:right;')}>
                        <span>{String(val)}</span>
                      </span>
                      <span style={sx('font-variant-numeric:tabular-nums;font-size:14px;text-align:right;color:' + (d === 0 ? '#54606C' : d > 0 ? '#1F6B52' : '#B3261E') + ';')}>
                        <span>{d === 0 ? '–' : d > 0 ? '+' + d : String(d)}</span>
                      </span>
                      <span style={sx('font-variant-numeric:tabular-nums;font-size:14px;font-weight:600;text-align:right;color:' + (fcv > last ? '#1F6B52' : fcv < last ? '#B3261E' : '#54606C') + ';')}>
                        <span>{String(fcv)}</span>
                      </span>
                    </a>
                  );
                })}
                <div style={sx('margin-top:10px;padding-top:9px;border-top:1px solid rgba(34,39,46,.12);')}>
                  <div style={sx('font-size:14px;font-weight:700;letter-spacing:.11em;text-transform:uppercase;color:#54606C;')}>
                    <span>{op ? 'Movers this quarter' : 'Where you stand'}</span>
                  </div>
                  {movers.map((m) => (
                    <a
                      key={m.k}
                      href="#"
                      title={m.tip}
                      onClick={(e) => {
                        pre(e);
                        m.onClick?.();
                      }}
                      style={sx('display:grid;grid-template-columns:104px minmax(0,1fr) auto;gap:8px;align-items:baseline;padding:5px 6px;margin:0 -6px;border-radius:6px;text-decoration:none;color:#22272E;animation:riseIn .3s cubic-bezier(.22,.61,.36,1);')}
                    >
                      <span style={sx('font-size:14px;color:#54606C;white-space:nowrap;')}>
                        <span>{m.k}</span>
                      </span>
                      <span style={sx('font-size:16px;font-weight:600;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;min-width:0;')}>
                        <span>{m.name}</span>
                      </span>
                      <span style={sx(V(m.color))}>
                        <span>{m.v}</span>
                      </span>
                    </a>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </BenchFrame>
  );
}
